<template>
  <div class="tab-panel tw:space-y-6">
    <div :class="tabStripClass" data-route-tabs>
      <router-link
        v-for="tab in tabs"
        :key="getTabKey(tab)"
        :to="getTabTo(tab)"
        custom
        v-slot="{ href, navigate, isActive }"
      >
        <a
          :href="href"
          @click="navigate"
          :class="getTabClass(isTabActive(tab, isActive))"
        >
          <font-awesome-icon
            v-if="getTabIcon(tab)"
            :icon="getTabIcon(tab)"
            class="tw:text-xs"
            :class="getIconClass(isTabActive(tab, isActive))"
          />
          <span>{{ getTabLabel(tab) }}</span>
        </a>
      </router-link>
    </div>

    <div v-if="showPanel" class="tw:w-full">
      <router-view v-slot="{ Component }">
        <keep-alive :include="includeInKeepAlive">
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouterViewMeta } from '@/composables/useRouterViewMeta'
import type { RouteLocationRaw } from 'vue-router'

interface CustomTab {
  id: string
  label: string
  icon?: unknown
  to: RouteLocationRaw
}

interface Props {
  variant?: 'primary' | 'secondary'
  tabs?: CustomTab[]
  activeTabId?: string
  showPanel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  showPanel: true
})

const { children, includeInKeepAlive } = useRouterViewMeta()

const tabs = computed(() => {
  if (props.tabs && props.tabs.length > 0) {
    return props.tabs
  }

  return children.value.filter(child => !child.redirect)
})

const useCustomTabs = computed(() => {
  return Array.isArray(props.tabs) && props.tabs.length > 0
})

const tabStripClass = computed(() => {
  if (props.variant === 'primary') {
    return 'tw:flex tw:items-center tw:gap-2 tw:w-fit tw:max-w-full tw:overflow-x-auto tw:scrollbar-none tw:rounded-2xl tw:border tw:border-slate-850 tw:bg-slate-950/30 tw:p-1 tw:shadow-premium'
  }

  return 'tw:border-b tw:border-slate-800/70 tw:overflow-x-auto tw:scrollbar-none tw:flex tw:gap-1 tw:px-1'
})

const getTabClass = (isActive: boolean) => {
  if (props.variant === 'primary') {
    return [
      'tw:px-4 tw:py-2 tw:text-sm tw:font-semibold tw:transition-all tw:duration-200 tw:border tw:rounded-xl tw:cursor-pointer tw:whitespace-nowrap tw:flex tw:items-center tw:gap-2 tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-indigo-400/70 tw:focus-visible:ring-offset-1 tw:focus-visible:ring-offset-slate-950',
      isActive
        ? 'tw:border-indigo-500/60 tw:text-white tw:bg-indigo-600 tw:shadow-lg tw:shadow-indigo-600/15'
        : 'tw:border-transparent tw:text-slate-400 tw:hover:text-slate-200 tw:hover:bg-slate-800/40'
    ]
  }

  return [
    'tw:px-3.5 tw:py-2.5 tw:text-sm tw:font-semibold tw:transition-all tw:duration-200 tw:border-b-2 tw:rounded-t-lg tw:cursor-pointer tw:whitespace-nowrap tw:flex tw:items-center tw:gap-2 tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-indigo-400/70 tw:focus-visible:ring-offset-1 tw:focus-visible:ring-offset-slate-950',
    isActive
      ? 'tw:border-indigo-500 rt-secondary-tab--active'
      : 'tw:border-transparent rt-secondary-tab--inactive'
  ]
}

const getIconClass = (isActive: boolean) => {
  if (props.variant === 'primary') {
    return isActive ? 'tw:text-indigo-100' : 'tw:text-slate-500'
  }

  return isActive ? 'rt-secondary-icon--active' : 'rt-secondary-icon--inactive'
}

const getTabKey = (tab: any) => {
  return useCustomTabs.value ? String(tab.id) : String(tab.name)
}

const getTabTo = (tab: any) => {
  return useCustomTabs.value ? tab.to : { name: tab.name as string }
}

const getTabLabel = (tab: any) => {
  return useCustomTabs.value ? tab.label : tab.meta?.label
}

const getTabIcon = (tab: any) => {
  return useCustomTabs.value ? tab.icon : tab.meta?.icon
}

const isTabActive = (tab: any, routerLinkIsActive: boolean) => {
  if (useCustomTabs.value && props.activeTabId) {
    return props.activeTabId === tab.id
  }

  return routerLinkIsActive
}
</script>

<style scoped>
.rt-secondary-tab--active {
  color: var(--color-slate-100);
  background-color: var(--color-slate-955);
}

.rt-secondary-tab--inactive {
  color: var(--color-slate-400);
}

.rt-secondary-tab--inactive:hover {
  color: var(--color-slate-200);
  background-color: var(--color-slate-955);
}

.rt-secondary-icon--active {
  color: var(--color-slate-100);
}

.rt-secondary-icon--inactive {
  color: var(--color-slate-500);
}
</style>